import axios from "axios";
import { getToken, clearToken } from "./auth";

// Create axios instance
const api = axios.create({
  baseURL: "/api",
  timeout: 300000, // Reduced since long-running tasks use background jobs
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add authorization header if token exists
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error("[API] Request error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`[API] ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error("[API] Response error:", error.response?.data || error.message);

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Clear auth token and redirect to login
      clearToken();
      // Only redirect if not already on login page
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);

// API types
export interface TopSymbol {
  symbol: string;
  stock_price: number;
  strike: number;
  mid_premium: number;
  contracts_affordable: number;
  total_premium_value: number;
  price_change_percent?: number;
  momentum?: number;
  momentum_4h?: number;
  momentum_1h?: number;
  momentum_30m?: number;
  expiry: string;
  sector?: string;
  metadata: {
    is_cached: boolean;
    timestamp: string | null;
    age_seconds: number | null;
  };
}

export interface ChartDataPoint {
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number | null;
}

export interface ChartData {
  symbol: string;
  data: ChartDataPoint[];
  metadata: {
    is_cached: boolean;
    timestamp: string | null;
    age_seconds: number | null;
  };
}

export interface SymbolSearchItem {
  symbol: string;
  list_type: string;
}

export interface SymbolSearch {
  results: SymbolSearchItem[];
  total: number;
}

export interface OptionsData {
  symbol: string;
  stock_price: number;
  expiry: string;
  call: any | null;
  put: any | null;
  metadata: {
    is_cached: boolean;
    timestamp: string | null;
    age_seconds: number | null;
  };
}

export interface SymbolList {
  lists: string[];
  descriptions: Record<string, string>;
}

export interface Position {
  id: string;
  symbol: string;
  stock_qty: number;
  purchase_price: number;
  premium_mark?: number | null;
  strike_price?: number | null;
  expire_date?: string | null;
  cash: number;
  premium: number;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  // Closed position fields
  is_closed: boolean;
  closed_at?: string | null;
  close_type?: string | null;
  closing_price?: number | null;
  realized_pnl?: number | null;
  closing_notes?: string | null;
  // Live market data fields
  current_price?: number;
  price_change_percent?: number;
  current_value?: number;
  unrealized_gain_loss?: number;
  unrealized_gain_loss_percent?: number;
}

export interface PositionCreate {
  symbol: string;
  stock_qty: number;
  purchase_price: number;
  premium_mark?: number | null;
  strike_price?: number | null;
  expire_date?: string | null;
  notes?: string | null;
}

export interface PositionClose {
  close_type: "expired" | "closed";
  closing_price: number;
  closing_date: string;
  closing_notes?: string | null;
}

export interface PositionResponse {
  position: Position;
  message: string;
}

export interface PositionsListResponse {
  positions: Position[];
  total_cash: number;
  total_premium: number;
  count: number;
}

export interface ClosedPositionsListResponse {
  positions: Position[];
  total_realized_pnl: number;
  count: number;
}

export interface BlacklistResponse {
  symbols: string[];
  count: number;
}

// Sentiment Forecast types
export interface ForecastResponse {
  symbol: string;
  asof: string;
  horizon_days: number;
  forecast: {
    mean_return: number;
    q20_return: number;
    q80_return: number;
    mean_price: number;
    forecast_days?: number[];
    q20_days?: number[];
    q80_days?: number[];
  };
  models: Record<string, { mean: number }>;
  confidence: number;
  current_price: number;
  sentiment?: {
    current: number;
    count: number;
    ema_3: number;
  };
}

export interface RiskResponse {
  symbol: string;
  abort: boolean;
  severity: "warn" | "critical" | "none";
  reasons: string[];
}

export interface BacktestResponse {
  symbol: string;
  weeks_backtested: number;
  horizon_days: number;
  metrics: {
    directional_accuracy: number;
    mape: number;
    rmse: number;
    hit_rate_q20_q80: number;
    precision_at_k: number;
    mean_error: number;
    n_predictions: number;
    high_confidence_count: number;
  };
}

export interface BacktestJobResponse {
  job_id: string;
  status: string;
  message: string;
}

export interface BacktestJobStatusResponse {
  job_id: string;
  status: "pending" | "running" | "completed" | "failed" | "cancelled";
  progress: number;
  message: string;
  result: BacktestResponse | null;
  error: string | null;
  created_at: string;
  eta_seconds?: number | null;
}

export interface ForecastJobResponse {
  job_id: string;
  status: string;
  message: string;
}

export interface ForecastJobStatusResponse {
  job_id: string;
  status: "pending" | "running" | "completed" | "failed";
  progress: number;
  message: string;
  result: ForecastResponse | null;
  error: string | null;
  created_at: string;
}

export interface RiskJobResponse {
  job_id: string;
  status: string;
  message: string;
}

export interface RiskJobStatusResponse {
  job_id: string;
  status: "pending" | "running" | "completed" | "failed";
  progress: number;
  message: string;
  result: RiskResponse | null;
  error: string | null;
  created_at: string;
}

export interface ChartJobResponse {
  job_id: string;
  status: string;
  message: string;
}

export interface ChartJobStatusResponse {
  job_id: string;
  status: "pending" | "running" | "completed" | "failed";
  progress: number;
  message: string;
  result: ChartData | null;
  error: string | null;
  created_at: string;
}

// Authentication types
export interface LoginCredentials {
  username: string;
  password: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
}

export interface UserResponse {
  id: string;
  username: string;
  email: string;
  is_active: boolean;
  created_at: string;
}

export interface AccessTokenResponse {
  access_token: string;
  message: string;
}

export interface AccessTokenMetadataResponse {
  has_token: boolean;
  created_at: string | null;
}

export interface MCPTool {
  name: string;
  description: string;
  inputSchema: {
    type: string;
    properties: Record<string, any>;
    required: string[];
  };
}

export interface MCPEndpointsResponse {
  tools: MCPTool[];
}

// API functions
export const apiClient = {
  // Authentication API
  login: async (credentials: LoginCredentials): Promise<TokenResponse> => {
    const response = await api.post("/auth/login", credentials);
    return response.data;
  },

  getCurrentUser: async (): Promise<UserResponse> => {
    const response = await api.get("/auth/me");
    return response.data;
  },

  logout: async (): Promise<void> => {
    clearToken();
  },
  // Get available symbol lists
  getSymbolLists: async (): Promise<SymbolList> => {
    const response = await api.get("/symbols/lists");
    return response.data;
  },

  // Get top symbols from a list
  getTopSymbols: async (
    listType: string,
    limit: number = 10,
    sortBy: string = "total_premium_value",
    initialCash: number = 370000,
    forceRefresh: boolean = false,
    sortOrder: string = 'desc'
  ): Promise<TopSymbol[]> => {
    const response = await api.get(`/symbols/${listType}/top`, {
      params: {
        limit,
        sort_by: sortBy,
        sort_order: sortOrder,
        initial_cash: initialCash,
        force_refresh: forceRefresh,
      },
    });
    return response.data;
  },

  // Get chart data for a symbol
  getChartData: async (
    symbol: string,
    period: string = "1y",
    interval: string = "1d",
    extendedHours: boolean = false
  ): Promise<ChartData> => {
    const response = await api.get(`/chart/${symbol}/history`, {
      params: { period, interval, extended_hours: extendedHours },
    });
    return response.data;
  },

  // Search symbols
  searchSymbols: async (query: string, limit: number = 20): Promise<SymbolSearch> => {
    const response = await api.get("/symbols/search", {
      params: { q: query, limit },
    });
    return response.data;
  },

  // Get options data for a symbol
  getOptionsData: async (symbol: string): Promise<OptionsData> => {
    const response = await api.get(`/options/${symbol}`);
    return response.data;
  },

  // Clear cache
  clearCache: async (
    symbol?: string
  ): Promise<{ message: string; cleared_symbols?: string[] }> => {
    const response = await api.post("/cache/clear", { symbol });
    return response.data;
  },

  // Health check
  healthCheck: async (): Promise<{ status: string; message: string }> => {
    const response = await api.get("/health");
    return response.data;
  },

  // Positions API
  createPosition: async (positionData: PositionCreate): Promise<PositionResponse> => {
    const response = await api.post("/positions", positionData);
    return response.data;
  },

  getPositions: async (): Promise<PositionsListResponse> => {
    const response = await api.get("/positions");
    return response.data;
  },

  getPosition: async (positionId: string): Promise<PositionResponse> => {
    const response = await api.get(`/positions/${positionId}`);
    return response.data;
  },

  updatePosition: async (
    positionId: string,
    positionData: PositionCreate
  ): Promise<PositionResponse> => {
    const response = await api.put(`/positions/${positionId}`, positionData);
    return response.data;
  },

  closePosition: async (
    positionId: string,
    closeData: PositionClose
  ): Promise<PositionResponse> => {
    const response = await api.post(`/positions/${positionId}/close`, closeData);
    return response.data;
  },

  getClosedPositions: async (): Promise<ClosedPositionsListResponse> => {
    const response = await api.get("/positions/closed");
    return response.data;
  },

  deletePosition: async (positionId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/positions/${positionId}`);
    return response.data;
  },

  deleteAllClosedPositions: async (): Promise<{ message: string; count: number }> => {
    const response = await api.delete("/positions/closed");
    return response.data;
  },

  // Blacklist API
  getBlacklist: async (): Promise<BlacklistResponse> => {
    const response = await api.get("/blacklist");
    return response.data;
  },

  addToBlacklist: async (symbol: string): Promise<BlacklistResponse> => {
    const response = await api.post("/blacklist/add", { symbol });
    return response.data;
  },

  removeFromBlacklist: async (symbol: string): Promise<BlacklistResponse> => {
    const response = await api.delete("/blacklist/remove", { data: { symbol } });
    return response.data;
  },

  // Sentiment Forecast API - Background Jobs
  startForecastJob: async (
    symbol: string,
    horizon: number = 5
  ): Promise<ForecastJobResponse> => {
    const response = await api.post(`/forecast/${symbol}/job`, null, {
      params: { horizon },
    });
    return response.data;
  },

  getForecastJobStatus: async (jobId: string): Promise<ForecastJobStatusResponse> => {
    const response = await api.get(`/forecast/job/${jobId}`);
    return response.data;
  },

  startRiskJob: async (symbol: string): Promise<RiskJobResponse> => {
    const response = await api.post(`/risk/${symbol}/job`);
    return response.data;
  },

  getRiskJobStatus: async (jobId: string): Promise<RiskJobStatusResponse> => {
    const response = await api.get(`/risk/job/${jobId}`);
    return response.data;
  },

  startChartJob: async (
    symbol: string,
    period: string = "1y",
    interval: string = "1d",
    extendedHours: boolean = false
  ): Promise<ChartJobResponse> => {
    const response = await api.post(`/chart/${symbol}/job`, null, {
      params: { period, interval, extended_hours: extendedHours },
    });
    return response.data;
  },

  getChartJobStatus: async (jobId: string): Promise<ChartJobStatusResponse> => {
    const response = await api.get(`/chart/job/${jobId}`);
    return response.data;
  },

  // Legacy direct endpoints (kept for backward compatibility)
  getForecast: async (symbol: string, horizon: number = 5): Promise<ForecastResponse> => {
    const response = await api.get(`/forecast/${symbol}`, {
      params: { horizon },
    });
    return response.data;
  },

  getRiskAssessment: async (symbol: string): Promise<RiskResponse> => {
    const response = await api.get(`/risk/${symbol}`);
    return response.data;
  },

  // Backtest API (manual execution)
  runBacktest: async (
    symbol: string,
    weeks: number = 104
  ): Promise<BacktestJobResponse> => {
    const response = await api.post(`/backtest/${symbol}`, null, {
      params: { weeks },
    });
    return response.data;
  },

  getBacktestJobStatus: async (jobId: string): Promise<BacktestJobStatusResponse> => {
    const response = await api.get(`/backtest/job/${jobId}`);
    return response.data;
  },

  cancelBacktest: async (jobId: string): Promise<{ message: string; job_id: string }> => {
    const response = await api.post(`/backtest/job/${jobId}/cancel`);
    return response.data;
  },

  // Access Token API
  generateAccessToken: async (): Promise<AccessTokenResponse> => {
    const response = await api.post("/auth/token/generate");
    return response.data;
  },

  regenerateAccessToken: async (): Promise<AccessTokenResponse> => {
    const response = await api.post("/auth/token/regenerate");
    return response.data;
  },

  getTokenMetadata: async (): Promise<AccessTokenMetadataResponse> => {
    const response = await api.get("/auth/token");
    return response.data;
  },

  // MCP Endpoints API (requires token in path, so we use axios directly)
  getMCPEndpoints: async (token: string): Promise<MCPEndpointsResponse> => {
    // Use base axios without /api prefix for MCP endpoints
    const response = await axios.get(`/mcp/${token}`);
    return response.data;
  },
};

export default apiClient;
